/**
 * StepDrillingMethod.jsx
 *
 * Tool Finder wizard step 2 (Explore path). Captures the user's drilling
 * method and stores it in `selections.drillingMethod`, deriving
 * `selections.productLine` from the same selection (the two are 1:1 for
 * TH and DTH today; ITH / Rotary / Raiseboring carry no productLine yet).
 *
 * Does NOT auto-advance — NavigationButtons drives Next/Back via
 * handleNextExplore.
 *
 * Taxonomy: imports `DRILLING_METHODS` from `@data/wizardData` (extended
 * from TH TF v4 DRILLING_TYPES with the full mining-method enum per
 * Vandana's domain correction, 17 May 2026). Domain reference:
 * `Claude-Enterprise-Migration-Package/03-sandvik-agents/.../kb/tophammer-dth-domain.md`
 * for the TH and DTH systems; ITH / Rotary / Raiseboring are filtered
 * out of the rendered tile set until their wizard branches ship.
 *
 * @module components/wizard/StepDrillingMethod
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D3b)
 */

import { useWizard } from '@contexts/WizardContext';
import TileSelector from '@components/ui/TileSelector';
import { DRILLING_METHODS } from '@data/wizardData';

// MRT-side UX additions — sublabels and text-chip icon placeholders.
// Real artwork lands in a later phase.
const METHOD_SUBLABELS = {
  TH:          'Above-hole impact drilling, T- and CT-series threads',
  DTH:         'In-hole percussion hammer, RH460 / RH510 / RH560 series',
  ITH:         'In-the-hole drilling, specialty applications',
  Rotary:      'Rotary drilling for large-diameter blastholes',
  Raiseboring: 'Pilot-and-ream drilling for shafts and orepasses',
};

const METHOD_ICON_CHIPS = {
  TH:          'TH',
  DTH:         'DTH',
  ITH:         'ITH',
  Rotary:      'ROT',
  Raiseboring: 'RB',
};

export default function StepDrillingMethod() {
  const { selections, handleSelect } = useWizard();

  // 17 May (Vandana): hide ITH / Rotary / Raiseboring tiles for now —
  // they'll be re-enabled when their catalogs and wizard branches ship.
  // The DRILLING_METHODS array keeps the full enum so wizardData stays
  // forward-compatible; only the rendered list is trimmed here.
  const tiles = DRILLING_METHODS
    .filter((method) => method.available)
    .map((method) => ({
      id: method.id,
      label: method.label,
      sublabel: METHOD_SUBLABELS[method.id] || '',
      icon: METHOD_ICON_CHIPS[method.id] || method.id.slice(0, 3),
    }));

  return (
    <section aria-labelledby="drilling-method-heading" className="max-w-5xl mx-auto">
      <header className="mb-6">
        <h2
          id="drilling-method-heading"
          className="font-sandvik-headline text-2xl font-normal text-carbon-black"
        >
          Which drilling method?
        </h2>
        <p className="mt-1 font-sandvik-text text-n6">
          Pick the drilling process you&apos;ll use. Today we cover Top Hammer and Down the Hole;
          In the Hole, Rotary, and Raiseboring will join as their catalogs come online.
        </p>
      </header>

      <TileSelector
        tiles={tiles}
        value={selections.drillingMethod}
        onChange={(id) => {
          const method = DRILLING_METHODS.find((m) => m.id === id);
          // Set both fields atomically via handleSelect's `extra` arg so we
          // never have an inconsistent state where drillingMethod !== productLine.
          handleSelect('drillingMethod', id, {
            productLine: method?.productLine ?? null,
          });
        }}
        cols={{ mobile: 2, tablet: 3, desktop: 3 }}
        variant="icon"
        ariaLabel="Drilling method"
      />
    </section>
  );
}
